/**
 * Web only Grove Fruit Slices Host
 *
 * Renders a Fruit Slice inside a sandboxed `<iframe srcdoc>` and answers its
 * bridge requests over postMessage. The slice runs origin-null; its only I/O is
 * via the exposed grove bridge defined below.
 */

import type { FruitSlice, FruitSliceFrame } from '@fruitful/types';
import React, { useEffect, useMemo, useRef } from 'react';

import { captureWarning } from '../../logger/Logger';
import { CAPABILITIES } from '../utils/capabilities';
import type { BridgeRequest, BridgeResult } from '../utils/runtime';
import { buildSrcDoc } from '../utils/runtime';
import {
    loadFruitSliceStorage,
    saveFruitSliceStorage,
} from '../utils/storageClient';

/**
 * Run a single bridge request and return its result (throws on failure).
 *   - `call`        dispatches to the capability registry
 *   - `load`/`save` fetch & persist the slice's own state blob
 */
async function resolveBridgeRequest(
    request: BridgeRequest,
    sliceId: string,
): Promise<unknown> {
    switch (request.method) {
        case 'call': {
            const resolve = CAPABILITIES[request.capability];
            if (!resolve) {
                captureWarning(
                    `Unknown Fruit Slice capability: ${request.capability}`,
                );
                return null;
            }
            return await resolve(request.params);
        }
        case 'load': {
            const blob = await loadFruitSliceStorage(sliceId);
            return blob[request.scope ?? 'default'] ?? null;
        }
        case 'save': {
            await saveFruitSliceStorage(
                sliceId,
                request.scope ?? 'default',
                request.data ?? null,
            );
            return true;
        }
    }
}

export function FruitSliceHost({
    slice,
    frame,
}: {
    slice: Pick<FruitSlice, 'id' | 'name'>;
    frame: Pick<FruitSliceFrame, 'html'>;
}) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const srcDoc = useMemo(() => buildSrcDoc(frame), [frame]);

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            const request = event.data as BridgeRequest | undefined;
            // Only answer bridge requests coming from this slice's own iframe.
            if (request?.__grove !== 'request') return;
            if (event.source !== iframeRef.current?.contentWindow) return;

            const respond = (payload: BridgeResult) =>
                (event.source as Window | null)?.postMessage(
                    { __grove: 'response', id: request.id, ...payload },
                    '*',
                );

            try {
                respond({
                    ok: true,
                    result: await resolveBridgeRequest(request, slice.id),
                });
            } catch (err) {
                respond({ ok: false, error: (err as Error).message });
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [slice.id]);

    return (
        <iframe
            ref={iframeRef}
            title={slice.name}
            sandbox="allow-scripts"
            srcDoc={srcDoc}
            style={{
                border: 'none',
                width: '100%',
                height: '100%',
                minHeight: '80vh',
                background: '#fff',
            }}
        />
    );
}
